import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { useHistory } from 'react-router';
import { Button, FormControl, Grid, Typography } from '@mui/material';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from '@material-ui/core';
import { Field, Form } from 'react-final-form';
import { useCallback, useState } from 'react';

import {
  selectUnidadeSelecionada,
  selectUnidadesDeNegocioAssociadas,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { GridItemModalProps } from '../../../common/components/cardsGrid/cardsModalGrid';
import { useUnidadeNegocioGuard } from '../../../common/hooks/useUnidadeNegocioGuard';
import { useListClientes } from '../../../common/hooks/useListClientes';
import MultiSelectStyled, { SimpleMultiSelectOption } from '../../../common/components/input/multiSelectStyled';
import QCXFinalDatePickerField from '../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import { fetchByFilter } from '../../../../features/follow-up/followUpAPI';
import { generateKpiExportacaoReport } from './kpiExportacaoPage.helpers';

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      minWidth: '50vw',
      minHeight: '40vh',
    },
  },
});

const KpiExportacaoPage = ({ title, isOpen, closeModal }: GridItemModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  keycloak.updateToken(300);

  const selectedUnit = useSelector(selectUnidadeSelecionada);
  const tenantList = useSelector(selectUnidadesDeNegocioAssociadas);
  const [clientes] = useListClientes();

  const [selectedTenants, setSelectedTenants] = useState<SimpleMultiSelectOption[]>([]);
  const [selectedClients, setSelectedClients] = useState<SimpleMultiSelectOption[]>([]);
  const [followUpList, setFollowUpList] = useState<SimpleMultiSelectOption[]>([]);
  const [selectedProcesses, setSelectedProcesses] = useState<SimpleMultiSelectOption[]>([]);
  const [isFetchingProcesses, setIsFetchingProcesses] = useState(false);
  const [periodo, setPeriodo] = useState({ inicio: '', fim: '' });

  useUnidadeNegocioGuard();

  const fetchFilteredFollowUps = useCallback(async (clients: SimpleMultiSelectOption[]) => {
    const ids = clients.map((c) => c.id).join(',');

    if (!ids) {
      setFollowUpList([]);
      return;
    }

    try {
      setIsFetchingProcesses(true);
      const resp = await fetchByFilter([
        { name: 'clientIds', value: ids },
        { name: 'incluirVinculadosDi', value: 'true' },
      ]);

      setFollowUpList(
        resp.data
          .filter((f: any) => !f.servico?.produto || f.servico.produto === 'EXPORTACAO')
          .map((f: any) => ({
            id: f.id?.toString() || '',
            value: f.numero?.toString() || '[Sem número]',
          }))
      );
    } finally {
      setIsFetchingProcesses(false);
    }
  }, []);

  const handleTenantsChange = (_e: any, value: SimpleMultiSelectOption[] | null) => setSelectedTenants(value || []);

  const handleClientsChange = (_e: any, value: SimpleMultiSelectOption[] | null) => {
    const novosClientes = value || [];
    setSelectedClients(novosClientes);
    setSelectedProcesses([]);
    fetchFilteredFollowUps(novosClientes);
  };

  const handleChangePeriodo = (campo: 'inicio' | 'fim') => (data: any) =>
    setPeriodo((anterior) => ({
      ...anterior,
      [campo]: data ? data.toISOString().split('T')[0] : '',
    }));

  const periodoIncompleto = periodo.inicio === '' || periodo.fim === '';

  const handleSubmit = async () => {
    keycloak.updateToken(300);

    if (selectedTenants.length === 0) {
      dispatch(setErrorFeedback({ message: 'Selecione pelo menos uma Unidade de Negócio.' }));
      return;
    }

    if (periodoIncompleto) {
      dispatch(setErrorFeedback({ message: 'Informe a data inicial e a data final do Registro da DU-E.' }));
      return;
    }

    if (new Date(periodo.inicio) > new Date(periodo.fim)) {
      dispatch(
        setErrorFeedback({
          message: 'A data inicial de Registro da DU-E não pode ser maior que a data final.',
        })
      );
      return;
    }

    if (token && selectedUnit) {
      const erro = await generateKpiExportacaoReport(token, selectedUnit, {
        tenantIds: selectedTenants.map((t) => t.id.toString()),
        clientIds: selectedClients.map((c) => c.id.toString()),
        processIds: selectedProcesses.map((p) => p.id.toString()),
        dataRegistroDueRange: `${periodo.inicio};${periodo.fim}`,
      });

      if (erro == null) {
        dispatch(setSuccessFeedback({ message: 'Relatório gerado com sucesso!' }));
      } else {
        dispatch(setErrorFeedback({ message: erro }));
      }
    }

    history.push('/exportacao/relatorios');
  };

  return (
    <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Form onSubmit={() => {}}>
          {() => (
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={selectedTenants.length === 0}>
                    <MultiSelectStyled
                      controlledValues={selectedTenants}
                      options={tenantList.map((t: { id: { toString: () => any }; pessoa: { nomeResumido: any } }) => ({
                        id: t.id.toString(),
                        value: t.pessoa.nomeResumido,
                      }))}
                      onChangeAction={handleTenantsChange}
                      label="Unidade de Negócio"
                    />
                    {selectedTenants.length === 0 && (
                      <Typography variant="body2" color="error">
                        Este campo é obrigatório
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      controlledValues={selectedClients}
                      options={clientes.map((c) => ({
                        id: c.id ?? '',
                        value: `(${c.pessoa!.cnpj}) ${c.pessoa!.nome}`,
                      }))}
                      onChangeAction={handleClientsChange}
                      label="Clientes"
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      controlledValues={selectedProcesses}
                      options={followUpList}
                      onChangeAction={(_: any, v: SimpleMultiSelectOption[] | null) => setSelectedProcesses(v || [])}
                      label="Processos"
                      disabled={selectedClients.length === 0 || isFetchingProcesses}
                      placeholder={
                        selectedClients.length === 0
                          ? 'Selecione um Cliente'
                          : isFetchingProcesses
                          ? 'Carregando...'
                          : 'Selecionar Processos'
                      }
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <Field
                    name="registroDueInicio"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="kpi-exportacao-registro-due-inicio"
                        name={input.name}
                        label="Data Registro DU-E (Início)"
                        required
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        onChange={(d: any) => {
                          handleChangePeriodo('inicio')(d);
                          input.onChange(d);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    name="registroDueFim"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="kpi-exportacao-registro-due-fim"
                        name={input.name}
                        label="Data Registro DU-E (Fim)"
                        required
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        onChange={(d: any) => {
                          handleChangePeriodo('fim')(d);
                          input.onChange(d);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </Form>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display="flex" gridGap={4}>
          <Button variant="outlined" color="primary" onClick={closeModal}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            fullWidth
            disabled={selectedTenants.length === 0 || periodoIncompleto}
          >
            Gerar Relatório
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default KpiExportacaoPage;
